import React from "react";
import { format, isSameDay } from "date-fns";
import { useNavigate } from "react-router-dom";
import { CalendarEvent } from "../../utils/calendarUtils";
import {
  getWeekDays,
  getEventsForDay,
  EVENT_COLORS,
  getEntityRoute,
  EVENT_TYPE_LABELS,
} from "../../utils/calendarUtils";

interface WeekViewProps {
  currentDate: Date;
  events: CalendarEvent[];
  onDayClick: (date: Date) => void;
}

const WeekView: React.FC<WeekViewProps> = ({
  currentDate,
  events,
  onDayClick,
}) => {
  const navigate = useNavigate();
  const days = getWeekDays(currentDate);
  const today = new Date();

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      <div className="grid grid-cols-7">
        {days.map((day) => {
          const isToday = isSameDay(day, today);
          const dayEvents = getEventsForDay(events, day);

          return (
            <div
              key={day.toISOString()}
              className="border-r border-gray-100 last:border-r-0"
            >
              {/* Day header */}
              <button
                onClick={() => onDayClick(day)}
                className={`w-full border-b border-gray-200 px-2 py-3 text-center hover:bg-gray-50 ${
                  isToday ? "bg-primary-50" : "bg-gray-50"
                }`}
              >
                <div className="text-xs font-medium uppercase text-gray-500">
                  {format(day, "EEE")}
                </div>
                <div
                  className={`mt-1 inline-flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold ${
                    isToday ? "bg-primary-600 text-white" : "text-gray-900"
                  }`}
                >
                  {format(day, "d")}
                </div>
              </button>

              {/* Events */}
              <div className="min-h-[300px] space-y-1 p-1.5">
                {dayEvents.map((event) => {
                  const colors = EVENT_COLORS[event.type];
                  return (
                    <button
                      key={event.id}
                      onClick={() =>
                        navigate(getEntityRoute(event.type, event.entityId))
                      }
                      className={`w-full rounded-md border-l-2 ${colors.border} ${colors.bg} p-2 text-left transition-shadow hover:shadow-sm`}
                    >
                      {event.startTime && (
                        <div className={`text-xs font-medium ${colors.text}`}>
                          {event.startTime}
                          {event.endTime ? ` — ${event.endTime}` : ""}
                        </div>
                      )}
                      <div className="truncate text-xs font-medium text-gray-900">
                        {event.title}
                      </div>
                      <div className={`mt-0.5 text-xs ${colors.text}`}>
                        {EVENT_TYPE_LABELS[event.type]}
                      </div>
                      {event.amount !== undefined && (
                        <div className="mt-0.5 text-xs font-medium text-gray-600">
                          £
                          {(event.amount / 100).toLocaleString("en-GB", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WeekView;
